import { useEffect, useState } from "react";
import { SettingsRequestError, settingsApi } from "./api";
import type { AdminSystemPrompt } from "./types";
import "./admin-libraries.css";

function saveError(error: unknown): string {
  if (error instanceof SettingsRequestError && error.status === 409) return "提示词已被其他管理员更新。你的草稿已保留，请核对后重试。";
  return "提示词保存失败，请稍后重试。";
}

export function AdminPromptSettings() {
  const [items, setItems] = useState<AdminSystemPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState<AdminSystemPrompt | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [conflicted, setConflicted] = useState(false);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());

  function load(): void {
    setLoading(true);
    setLoadError("");
    void settingsApi.loadAdminSystemPrompts().then(setItems).catch(() => setLoadError("无法读取系统提示词，请稍后重试。"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  function open(item: AdminSystemPrompt): void {
    setEditing(item);
    setDraft(item.value);
    setError("");
    setConflicted(false);
    setRequestId(crypto.randomUUID());
  }

  async function refreshConflict(): Promise<void> {
    if (!editing) return;
    setSaving(true);
    try {
      const latest = (await settingsApi.loadAdminSystemPrompts()).find((item) => item.key === editing.key);
      if (!latest) throw new Error("admin_prompt_missing_after_conflict");
      setItems((current) => current.map((item) => item.key === latest.key ? latest : item));
      setEditing(latest);
      setConflicted(false);
      setError("已载入最新版本；你的正文草稿仍保留，请核对后保存。");
      setRequestId(crypto.randomUUID());
    } catch { setError("无法载入最新版本，草稿仍保留，请稍后重试。"); }
    finally { setSaving(false); }
  }

  async function save(): Promise<void> {
    if (!editing || !draft.trim()) {
      setError("提示词正文不能为空。");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const updated = await settingsApi.updateAdminSystemPrompt(editing.key, draft, editing.revision, requestId);
      setItems((current) => current.map((item) => item.key === updated.key ? { ...item, ...updated } : item));
      setEditing(null);
    } catch (cause) {
      setError(saveError(cause));
      setConflicted(cause instanceof SettingsRequestError && cause.status === 409);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="panel-state" role="status">正在读取系统提示词…</p>;
  if (loadError) return <div className="panel-error" role="alert"><p>{loadError}</p><button className="secondary outlined" type="button" onClick={load}>重试</button></div>;
  return <div className="admin-library-stack">
    {items.length === 0 ? <p className="panel-state">暂无系统提示词。</p> : <div className="admin-library-list">
      {items.map((item) => <article className="admin-library-row" key={item.key}>
        <div><strong>{item.label}</strong><small>{item.key} · {item.value_kind}{item.editable ? "" : ` · ${item.immutable_reason ?? "只读"}`}</small></div>
        <button className="secondary outlined" type="button" onClick={() => open(item)}>{item.editable ? "编辑" : "查看"}</button>
      </article>)}
    </div>}
    {editing ? <div className="admin-editor-backdrop" role="presentation">
      <section className="admin-editor-dialog" role="dialog" aria-modal="true" aria-labelledby="admin-prompt-editor-title">
        <header><div><h3 id="admin-prompt-editor-title">{editing.editable ? "编辑" : "查看"}提示词：{editing.label}</h3><p>当前版本 {editing.revision}</p></div><button type="button" aria-label="关闭提示词编辑器" disabled={saving} onClick={() => setEditing(null)}>×</button></header>
        <label>正文<textarea autoFocus value={draft} readOnly={!editing.editable} disabled={saving} onChange={(event) => setDraft(event.target.value)} /></label>
        {error ? <p className="action-error" role="alert">{error}</p> : null}
        {conflicted ? <button className="secondary outlined admin-conflict-refresh" type="button" disabled={saving} onClick={() => void refreshConflict()}>载入最新版本（保留草稿）</button> : null}
        <div className="admin-editor-actions"><button className="secondary outlined" type="button" disabled={saving} onClick={() => setEditing(null)}>{editing.editable ? "取消" : "关闭"}</button>{editing.editable ? <button className="primary" type="button" disabled={saving || draft === editing.value} onClick={() => void save()}>{saving ? "保存中…" : "保存"}</button> : null}</div>
      </section>
    </div> : null}
  </div>;
}
